import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDateIT } from "@/lib/format";
import { toast } from "sonner";
import { Loader2, AlertTriangle, Pencil, Calendar as CalIcon } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { it as itLocale } from "date-fns/locale";
import { RejectDialog } from "@/components/RejectDialog";
import { useFromState } from "@/lib/navContext";
import { usePersistedFilters } from "@/hooks/usePersistedFilters";
import { useScrollRestore } from "@/hooks/useScrollRestore";

export const CAT_LABELS = {
  segnalazione_negativa: ["Segnalazione negativa", "bg-rose-100 text-rose-800 border-rose-200"],
  richieste_crif: ["Richieste in CRIF", "bg-amber-100 text-amber-800 border-amber-200"],
  vincolo_cliente: ["Richiesta del cliente", "bg-sky-100 text-sky-800 border-sky-200"],
  motivo_da_chiarire: ["Motivo da chiarire", "bg-slate-100 text-slate-600 border-slate-300 border-dashed"],
  motivo_mancante: ["Motivo mancante", "bg-rose-50 text-rose-600 border-rose-300 border-dashed"],
  altro: ["Altro motivo", "bg-slate-100 text-slate-700 border-slate-200"],
};

export function CatBadge({ cat }) {
  const [label, cls] = CAT_LABELS[cat] || [cat, "bg-slate-100 text-slate-600 border-slate-200"];
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-[3px] border ${label ? cls : ""}`}>{label}</span>;
}

function EsitoDateEdit({ p, onSaved }) {
  const [open, setOpen] = useState(false);
  const save = async (d) => {
    if (!d) return;
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    try {
      await api.patch(`/monthly/${p.id}`, { esitoDate: iso });
      setOpen(false);
      toast.success(`Data effettiva rifiuto salvata: ${fmtDateIT(iso)}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid={`rej-esito-edit-${p.id}`}
          className={`inline-flex items-center gap-1 text-[12px] tabular-nums border-b border-dashed py-0.5 min-h-[30px] ${
            p.esito_date ? "text-slate-800 border-slate-300 hover:border-[#B7955B]" : "text-rose-600 border-rose-300 font-medium"}`}>
          <CalIcon className="w-3 h-3 text-[#B7955B]" />
          {p.esito_date ? fmtDateIT(p.esito_date) : "da recuperare"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <CalendarPicker mode="single" locale={itLocale}
          selected={p.esito_date ? new Date(`${p.esito_date}T00:00:00`) : undefined}
          defaultMonth={p.esito_date ? new Date(`${p.esito_date}T00:00:00`) : undefined} onSelect={save} />
      </PopoverContent>
    </Popover>
  );
}

function FinEdit({ p, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(p.finanziaria || "");
  const save = async () => {
    setEditing(false);
    if ((v || "").trim() === (p.finanziaria || "")) return;
    try {
      await api.patch(`/monthly/${p.id}`, { finanziaria: v.trim() });
      toast.success("Finanziaria salvata");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Salvataggio non riuscito"); }
  };
  if (editing) return (
    <input autoFocus value={v} onChange={(e) => setV(e.target.value)} onBlur={save}
      onKeyDown={(e) => e.key === "Enter" && save()} data-testid={`rej-fin-input-${p.id}`}
      className="border border-slate-300 rounded-[3px] px-1.5 py-1 text-xs w-32" />
  );
  return (
    <button onClick={() => setEditing(true)} data-testid={`rej-fin-edit-${p.id}`}
      className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-[#B7955B] min-h-[30px]">
      {p.finanziaria || <span className="text-slate-400">da indicare</span>} <Pencil className="w-3 h-3 text-slate-400" />
    </button>
  );
}

export default function RejectedTab() {
  const [d, setD] = useState(null);
  const [editReject, setEditReject] = useState(null);
  const fromState = useFromState();
  const [f, setF] = usePersistedFilters("pa-rifiutate", { dal: "", al: "", prodotto: "", categoria: "", collegata: "", solo_personali: false, rivalutazione_dovuta: false });

  const load = useCallback(async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v !== "" && v !== false));
    const r = await api.get("/portfolio-active/rejected", { params });
    setD(r.data);
  }, [f]);
  useEffect(() => { load().catch(() => setD({ items: [] })); }, [load]);
  useScrollRestore(!!d);

  if (!d) return <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const qd = d.qualita_dati || {};
  const sel = "border border-slate-300 rounded-[3px] px-2 py-1.5 text-sm bg-white";

  return (
    <div className="space-y-3" data-testid="pa-rejected">
      <div className="text-xs bg-slate-50 border border-slate-200 rounded-[3px] p-2.5 text-slate-600">
        Tutto lo storico ({d.totale_storico} pratiche, tutti i mesi), incluse persone NON personali — il campo "Cliente personale" non viene mai modificato da qui.
        Vista di consultazione: nessuna analisi AI, nessuna azione automatica.
      </div>
      {(qd.senza_data_esito > 0 || qd.senza_motivo > 0 || qd.senza_collegamento > 0) && (
        <div className="text-xs bg-amber-50 border border-amber-200 rounded-[3px] p-2.5 text-amber-800 flex items-start gap-1.5" data-testid="rej-quality">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Qualità dati: {qd.senza_data_esito} senza data effettiva rifiuto (rivalutazione non calcolabile) · {qd.senza_motivo} senza motivo · {qd.motivo_da_chiarire} da chiarire · {qd.senza_finanziaria} senza finanziaria · {qd.senza_collegamento} senza anagrafica collegata. Nessun dato viene inventato per far quadrare i contatori.</span>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <input type="month" value={f.dal} onChange={(e) => setF({ ...f, dal: e.target.value })} className={sel} data-testid="rej-dal" />
        <span className="text-slate-400">→</span>
        <input type="month" value={f.al} onChange={(e) => setF({ ...f, al: e.target.value })} className={sel} />
        <select value={f.prodotto} onChange={(e) => setF({ ...f, prodotto: e.target.value })} className={sel} data-testid="rej-prodotto">
          <option value="">Prodotto: tutti</option><option>PP</option><option>CQS</option>
        </select>
        <select value={f.categoria} onChange={(e) => setF({ ...f, categoria: e.target.value })} className={sel} data-testid="rej-categoria">
          <option value="">Motivo: tutti</option>
          {Object.entries(CAT_LABELS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={f.collegata} onChange={(e) => setF({ ...f, collegata: e.target.value })} className={sel}>
          <option value="">Anagrafica: tutte</option><option value="si">Collegate</option><option value="no">Non collegate</option>
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-700">
          <input type="checkbox" checked={f.solo_personali} onChange={(e) => setF({ ...f, solo_personali: e.target.checked })} data-testid="rej-solo-personali" /> Solo clienti personali
        </label>
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-700">
          <input type="checkbox" checked={f.rivalutazione_dovuta} onChange={(e) => setF({ ...f, rivalutazione_dovuta: e.target.checked })} data-testid="rej-dovuta" /> Rivalutazione dovuta
        </label>
        <span className="text-xs text-slate-500 ml-auto">{d.filtrate} di {d.totale_storico}</span>
      </div>

      <div className="space-y-2">
        {(d.items || []).map((p) => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-[3px] p-3" data-testid={`rej-row-${p.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              {p.collegata
                ? <Link to={`/clienti/${p.client_id}`} state={fromState} className="font-semibold text-sm text-slate-900 hover:text-[#B7955B]">{p.cliente}</Link>
                : <span className="font-semibold text-sm text-slate-900">{p.cliente} <span className="text-[10px] text-amber-700 font-normal">(anagrafica non collegata: da confermare in Pratiche del Mese)</span></span>}
              {p.personale && <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] bg-[#35008B] text-white">personale</span>}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-[3px] border ${p.stato === "RIFIUTATA" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{p.stato}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] bg-slate-100 text-slate-600">{p.prodotto}</span>
              {p.motivo_label
                ? <span className="text-[10px] px-1.5 py-0.5 rounded-[3px] bg-[#0A192F] text-white" data-testid={`rej-motivo-${p.id}`}>{p.motivo_label}</span>
                : <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">suggerimento: <CatBadge cat={p.categoria} /></span>}
              {p.automazione && (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-[3px] border ${
                  p.automazione.status === "done" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : p.automazione.status === "pending_link" ? "bg-amber-50 text-amber-800 border-amber-200"
                      : "bg-rose-50 text-rose-700 border-rose-200"}`} data-testid={`rej-automazione-${p.id}`}>
                  {p.automazione.status === "done" ? "automazione eseguita" : p.automazione.status === "pending_link" ? "automazione in attesa di collegamento" : "automazione parziale"}
                </span>
              )}
              <button onClick={() => setEditReject(p)} data-testid={`rej-open-dialog-${p.id}`}
                className="text-[11px] px-2 py-1 border border-slate-300 rounded-[3px] hover:bg-slate-50 text-slate-700">
                {p.motivo_id ? "Modifica motivo" : "Registra motivo"}
              </button>
              <Link to={`/pipeline?mk=${p.monthKey}&focus=${p.id}`} state={fromState} className="text-[11px] text-slate-500 underline decoration-dotted ml-auto">pratica {p.monthKey}</Link>
            </div>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-x-4 gap-y-1 text-xs text-slate-600">
              <div>Caricata: <b className="tabular-nums">{p.caricata_il ? fmtDateIT(p.caricata_il) : "—"}</b></div>
              <div className="flex items-center gap-1">Rifiuto effettivo: <EsitoDateEdit p={p} onSaved={load} /></div>
              <div className="flex items-center gap-1">Finanziaria: <FinEdit p={p} onSaved={load} /></div>
              {p.banca_documento && <div className="text-slate-500">Banca dal documento: {p.banca_documento}</div>}
              <div>Rivalutazione: {p.rivalutazione
                ? <b className={`tabular-nums ${p.rivalutazione_dovuta ? "text-emerald-700" : ""}`}>{fmtDateIT(p.rivalutazione)}{p.rivalutazione_dovuta ? " (dovuta)" : ""}</b>
                : <span className="text-rose-600">non calcolabile senza data rifiuto</span>}</div>
            </div>
            <div className="mt-1.5 text-xs text-slate-700">
              <span className="text-slate-500">Motivo originale{p.fonte_motivo ? ` (${p.fonte_motivo})` : ""}:</span> {p.motivo || <span className="italic text-slate-400">nessuna nota</span>}
            </div>
            {p.tentativi_successivi?.length > 0 && (
              <div className="mt-1 text-[11px] text-slate-600">
                Tentativi successivi: {p.tentativi_successivi.map((t) => `${t.prodotto} ${t.monthKey} → ${t.esito}`).join(" · ")}
              </div>
            )}
            <div className="mt-1.5 text-xs font-medium text-[#0A192F] bg-slate-50 border border-slate-100 rounded-[3px] px-2 py-1.5" data-testid={`rej-azione-${p.id}`}>
              Prossima azione: {p.prossima_azione}
            </div>
          </div>
        ))}
        {(d.items || []).length === 0 && <div className="py-8 text-center text-slate-400 text-sm bg-white border border-slate-200 rounded-[3px]">Nessuna pratica rifiutata nei filtri scelti</div>}
      </div>
      {editReject && (
        <RejectDialog
          practice={{ id: editReject.id, monthKey: editReject.monthKey, cliente: editReject.cliente,
                      esitoDate: editReject.esito_date, rejectReasonId: editReject.motivo_id,
                      rejectNote: editReject.motivo_id ? editReject.motivo : "" }}
          onClose={() => setEditReject(null)}
          onDone={() => { setEditReject(null); load(); }} />
      )}
    </div>
  );
}
